import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { sendCarriageUpdate } from "~/features/configurator/Carriage/api/sendCarriageConfiguratorUpdate";
import {
  sendCarriageMiscChange,
  sendCarriageStepChange,
} from "~/features/configurator/Carriage/api/sendCarriageStepChange";
import { sendCarriageDataReset } from "~/features/configurator/Carriage/api/sendCarriageStepReset";
import { sendCarriageResetConfigurator } from "~/features/configurator/Carriage/api/sendCarriageResetConfigurator";
import { sendCarriageAddToCart } from "~/features/configurator/Carriage/api/sendCarriageAddToCart";
import { CarriageSummaryData } from "~/features/configurator/Carriage/v2/configuration/kwlt/summary/KWLTCarriageSummaryData";
import type { RootState } from "~/app/store";
import {
  BASE_DESIGNATION_SEPARATOR,
  CENTRAL_CONNECTORS_LABEL,
  CENTRAL_CONNECTORS_VALUE,
  MANUAL_CONNECTORS_LABEL,
  MANUAL_CONNECTORS_VALUE,
} from "~/features/configurator/Linear/constants";
import { KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import * as CarriageCstic from "~/features/configurator/Carriage/v2/configuration/kwlt/KWLTCarriageCstics";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { getCsticsValue } from "~/features/configurator/common/v2/helper";
import { findSelectedTileIndex } from "~/features/configurator/Linear/utils";
import { type LinearConfiguration, type LinearSubstep } from "~/features/configurator/Linear/models";
import { validateFromDefinition } from "~/features/configurator/common/v2/validation";
import {
  getStep1SummaryData,
  getStep2SummaryData,
} from "~/features/configurator/Carriage/v2/configuration/kwlt/summary/KWLTSummaryDataProvider";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import {
  prepareSubStepsInfo,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";

const CARRIAGE_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.Series, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = CARRIAGE_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = CARRIAGE_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const KWLT_CARRIAGE_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Carriage,
  productCode: KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.carriage.name",
  },
  apiCalls: {
    update: sendCarriageUpdate,
    reset: sendCarriageResetConfigurator,
    stepChange: sendCarriageStepChange,
    stepReset: sendCarriageDataReset,
    miscChange: sendCarriageMiscChange,
    addToCart: sendCarriageAddToCart,
  },
  summary: CarriageSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;

    const conflicts = state.linearConfigurator.conflicts;
    const validations = [0, 1].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );
    const addToCartEnabled = validations.every((validation) => validation);
    return addToCartEnabled;
  },
  isSaveEnabled: (state: RootState) => {
    return KWLT_CARRIAGE_DEFINITION.isAddToCartEnabled(state);
  },
  stepHardcodedData: CARRIAGE_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [materialName, sizeCode, version] = getCsticsValue(
      kmat,
      CarriageCstic.DesignType_MaterialName,
      CarriageCstic.DesignType_SizeCode,
      CarriageCstic.DesignType_Version,
    );

    const base = `${materialName || ""}${sizeCode || ""}`;
    const designation = version ? `${base}-${version}` : base;

    return designation || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-carriage",
  modelAndUpdatePreprocessor: (model, dto) => {
    const mainKmat = getMainKmat({ kmats: model.kmats });
    const cstics = mainKmat.cstics;

    const lubricationTypeCstic = mainKmat.cstics[CarriageCstic.LubricationConnectorType];
    if (lubricationTypeCstic) {
      const mapping: Record<string, string> = {
        [MANUAL_CONNECTORS_VALUE]: MANUAL_CONNECTORS_LABEL,
        [CENTRAL_CONNECTORS_VALUE]: CENTRAL_CONNECTORS_LABEL,
      };
      lubricationTypeCstic.values?.forEach((val) => {
        val.label = mapping[val.value];
      });
    }

    const designValue = cstics[CarriageCstic.MaterialName]?.value;
    const designSuffix = "." + designValue?.replace("/./g", "");

    const kitsLeftCstic = cstics[CarriageCstic.KitsLeft];
    if (kitsLeftCstic) {
      kitsLeftCstic.label = "configurator.carriage.cstic.kit.left.name";
      kitsLeftCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }
    const kitsCenterCstic = cstics[CarriageCstic.KitsCenter];
    if (kitsCenterCstic) {
      kitsCenterCstic.label = "configurator.carriage.cstic.kit.center.name";
      kitsCenterCstic.info = "configurator.carriage.cstic.kit.center.info" + designSuffix;
    }

    const kitsRightCstic = cstics[CarriageCstic.KitsRight];
    if (kitsRightCstic) {
      kitsRightCstic.label = "configurator.carriage.cstic.kit.right.name";
      // "left" on purpose
      kitsRightCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }

    const lubricationConnectorCstic = cstics[CarriageCstic.LubricationConnector];
    if (lubricationConnectorCstic) {
      lubricationConnectorCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorCstic.values?.forEach((val) => {
        val.imageUrl = `configurator.carriage.cstic.lubricationConnector.${val.value}.image`;
      });
    }

    const lubricationConnectorStatusCstic = cstics[CarriageCstic.LubricationConnectorOrientation];
    if (lubricationConnectorStatusCstic) {
      // Label key on purpose
      lubricationConnectorStatusCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorStatusCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.lubricationConnectorStatus.${val.value}.name`;
      });
    }

    return model;
  },
  postDefinitionProcessor: (model) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
} as const;

function pageIsConfigured(model: LinearConfiguration | LinearConfiguration, page: number): boolean {
  return (model.steps[page]!.substeps ?? []).every((substepType) => {
    const substep = model.substeps[substepType];

    substep.selectedTile = findSelectedTileIndex(substep as LinearSubstep, model.kmats);

    return validateFromDefinition(
      substep as LinearSubstep,
      model.kmats,
      (model as LinearConfiguration).conflictedCstics ?? [],
    ).substepValid;
  });
}

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const canVisitStep2 = pageIsConfigured(model, 0);
  const canVisitStep3 = pageIsConfigured(model, 1);

  const allowedSteps = [
    [true, canVisitStep2, canVisitStep3],
    [true, true, canVisitStep3],
    [true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(KWLT_CARRIAGE_DEFINITION);
